import { useMemo, useState } from 'react'

import { Dialog, DialogOverlay, DialogContent } from '@wartek-id/dialog-v1'
import { Text } from '@wartek-id/text-v1'
import { Button } from '@wartek-id/button-v1'
import { Icon } from '@wartek-id/icon-v2'
import { Shimmer } from '@wartek-id/shimmer'

import { CardSchool } from 'app/KinerjaPage/components/CardSchool'
import { PENGELOLAAN_KINERJA_KS_TIM_KINERJA_PATH } from 'app/KinerjaShared/constants/path'
import { useApiGetPenilaianSchoolList } from 'api/kinerja/kepala-sekolah/school'
import CardPenilaianPeriodik from 'app/KinerjaShared/components/CardPenilaianPeriodik'
import useToggleSdkContext from 'utils/useToogleSdk'
import { useApiGetPenilaianPeriodikType } from 'api/kinerja/kepala-sekolah/penilaian-periodik'
import usePeriodContext from 'app/KinerjaShared/hooks/usePeriodContext'

export function PeriodikTimKinerjaCard() {
  const { periodYear } = usePeriodContext()

  // ------------------
  // Resolve toggle value
  // ------------------
  const toggleValue = useToggleSdkContext()
  const isPeriodikAsnSwastaEnabled =
    toggleValue?.['kinerja-asn-swasta-periodik-enabled']

  // ------------------
  // Get penilaian periodik flag
  // ------------------
  const {
    data: periodikTypeData,
    isSuccess: isPeriodikTypeSuccess,
    isLoading: isPeriodikTypeLoading,
  } = useApiGetPenilaianPeriodikType({
    params: { skpYear: periodYear, tipe: 'SPF' },
    options: {
      enabled: isPeriodikAsnSwastaEnabled,
      staleTime: 60 * 60 * 1000, // 1 hour
      cacheTime: 60 * 60 * 1000, // 1 hour
    },
  })

  const periodikType = periodikTypeData?.data?.periodicType
  const hasPenilaianPeriodik =
    isPeriodikAsnSwastaEnabled &&
    isPeriodikTypeSuccess &&
    periodikType !== 'NOT_FOUND'

  // ------------------
  // Get school list
  // ------------------
  const { data: schoolListData, isLoading: isSchoolListLoading } =
    useApiGetPenilaianSchoolList({
      params: { tipe: 'SPF' },
      options: {
        staleTime: 60 * 60 * 1000, // 1 hour
      },
    })

  // Only show swasta schools
  const swastaSchools = useMemo(
    () =>
      schoolListData?.data?.filter(
        (school) => school.statusSatuanPendidikan === 'SWASTA'
      ) || [],
    [schoolListData]
  )

  const hasSwastaSchools = swastaSchools.length > 0

  // ------------------
  // Handle school selection dialog
  // ------------------
  const [isSchoolSelectionDialogOpen, setSchoolSelectionDialogOpen] =
    useState(false)

  const handleCloseSchoolSelectionDialog = () => {
    setSchoolSelectionDialogOpen(false)
  }

  if (isPeriodikTypeLoading || isSchoolListLoading) {
    return <Shimmer className="!h-36 !w-full block rounded-lg" />
  }

  if (!hasPenilaianPeriodik || !hasSwastaSchools) {
    return null
  }

  return (
    <>
      <CardPenilaianPeriodik
        ctaColor="black"
        ctaText="Lakukan Penilaian"
        periodicType={periodikType}
        onCtaClick={() => setSchoolSelectionDialogOpen(true)}
      />
      <Dialog
        isOpen={isSchoolSelectionDialogOpen}
        onClose={handleCloseSchoolSelectionDialog}
        customSize={596}
        autoFocus={false}
      >
        <DialogOverlay />
        <DialogContent
          className="rounded-lg m-auto self-center"
          aria-labelledby="periodik-pilih-satdik-title"
        >
          <div className="rounded-t-lg flex p-6 justify-between items-center bg-surface-subdued">
            <Text as="h2" variant="heading-md" id="periodik-pilih-satdik-title">
              Pilih satuan pendidikan
            </Text>
            <Button
              color="white"
              iconOnly
              size="sm"
              variant="ghost"
              className="!outline-none"
              onClick={handleCloseSchoolSelectionDialog}
            >
              <Icon color="inherit">close</Icon>
            </Button>
          </div>
          <div className="flex py-4 px-6 flex-col gap-4 max-h-[640px] overflow-y-auto">
            {swastaSchools.map((school) => (
              <CardSchool
                fullWidth
                key={school.npsn}
                ctaText="Lakukan Penilaian"
                ctaLabel={`Lakukan Penilaian ${school.schoolName}`}
                baseHref={`${PENGELOLAAN_KINERJA_KS_TIM_KINERJA_PATH}/periodik`}
                school={{
                  ...school,
                  perencanaanReadyState: 'COMPLETED', // Note: dummy since it wont used in this card
                }}
              />
            ))}
          </div>

          <div className="p-6">
            <Button
              fullWidth
              color="white"
              onClick={handleCloseSchoolSelectionDialog}
              type="button"
            >
              Kembali
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}
